import React, { useEffect, useState, useCallback } from "react";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Reusable media picker + inline uploader. Returns media_id via onSelect.
export function MediaPicker({ open, onOpenChange, onSelect }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(() => api.get("/media").then(({ data }) => setItems(data || [])), []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("category", "Autre");
    try { await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } }); await load(); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto custom-scroll">
        <DialogHeader><DialogTitle>Bibliothèque de médias</DialogTitle></DialogHeader>
        <div className="mb-4">
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm cursor-pointer" data-testid="media-upload">
            {uploading ? "Téléversement..." : "Téléverser une image"}
            <input type="file" accept="image/*" className="hidden" onChange={upload} />
          </label>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {onSelect && <button onClick={() => { onSelect(null); onOpenChange(false); }} className="aspect-square rounded-lg border-2 border-dashed border-slate-200 text-xs text-slate-400 hover:border-slate-300">Aucune / défaut</button>}
          {items.map((m) => (
            <button key={m.media_id} onClick={() => { onSelect && onSelect(m.media_id, m); onOpenChange(false); }}
              className="group relative aspect-square rounded-lg overflow-hidden border border-slate-200 hover:ring-2 hover:ring-primary" data-testid={`media-item-${m.media_id}`}>
              <img src={`${API}/media/file/${m.media_id}`} alt={m.alt} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ImageField({ label, value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <span className="text-xs text-slate-500">{label}</span>
      <div className="mt-1 flex items-center gap-3">
        <div className="h-14 w-14 rounded-lg border border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center">
          {value ? <img src={`${API}/media/file/${value}`} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-400">Défaut</span>}
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>Choisir</Button>
        {value && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => onChange(null)}>Retirer</Button>}
      </div>
      <MediaPicker open={open} onOpenChange={setOpen} onSelect={(id) => onChange(id)} />
    </div>
  );
}

// Image field with live focal-point / fit / zoom editor.
// `value` = media_id ; `pos` = { x, y, fit, zoom } ; onChange(id) ; onPos(posObj)
export function ImageFocalField({ label, value, onChange, pos, onPos, testId = "focal" }) {
  const [open, setOpen] = useState(false);
  const p = pos || {};
  const x = p.x ?? 50, y = p.y ?? 50, fit = p.fit || "cover", zoom = p.zoom ?? 1;
  const setP = (patch) => onPos({ x, y, fit, zoom, ...patch });
  const preview = value ? `${API}/media/file/${value}` : null;

  const pick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const nx = Math.min(100, Math.max(0, Math.round(((e.clientX - r.left) / r.width) * 100)));
    const ny = Math.min(100, Math.max(0, Math.round(((e.clientY - r.top) / r.height) * 100)));
    setP({ x: nx, y: ny });
  };
  const btn = (active) => `px-2.5 py-1 rounded-md border text-xs ${active ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`;

  return (
    <div data-testid={`${testId}-field`}>
      <span className="text-xs text-slate-500">{label}</span>
      <div className="mt-1 relative rounded-lg border border-slate-200 overflow-hidden bg-slate-50 h-40"
        style={{ cursor: preview ? "crosshair" : "default" }} onClick={preview ? pick : undefined}
        data-testid={`${testId}-preview`}>
        {preview ? (
          <img src={preview} alt="" className="w-full h-full select-none pointer-events-none"
            style={{ objectFit: fit, objectPosition: `${x}% ${y}%`, transform: zoom !== 1 ? `scale(${zoom})` : undefined, transformOrigin: `${x}% ${y}%` }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">Image par défaut</div>
        )}
        {preview && (
          <div className="absolute h-4 w-4 -ml-2 -mt-2 rounded-full bg-primary/80 ring-2 ring-white shadow pointer-events-none"
            style={{ left: `${x}%`, top: `${y}%` }} />
        )}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`${testId}-choose`}>Choisir</Button>
        {value && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => onChange(null)} data-testid={`${testId}-remove`}>Retirer</Button>}
      </div>
      {value && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="w-16 text-xs text-slate-500">Cadrage</span>
            <button type="button" className={btn(fit === "cover")} onClick={() => setP({ fit: "cover" })} data-testid={`${testId}-fit-cover`}>Remplir</button>
            <button type="button" className={btn(fit === "contain")} onClick={() => setP({ fit: "contain" })} data-testid={`${testId}-fit-contain`}>Image entière</button>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <span className="w-16 text-slate-500">Zoom</span>
            <input type="range" min="1" max="2.5" step="0.05" value={zoom} onChange={(e) => setP({ zoom: parseFloat(e.target.value) })} className="flex-1 accent-[var(--primary)]" data-testid={`${testId}-zoom`} />
            <span className="w-10 text-right text-slate-500">{zoom.toFixed(2)}×</span>
          </label>
          <div className="flex items-center gap-3 text-xs">
            <label className="flex items-center gap-1"><span className="text-slate-500">X %</span>
              <input type="number" min="0" max="100" value={x} onChange={(e) => setP({ x: Math.min(100, Math.max(0, +e.target.value || 0)) })} className="w-16 h-8 border border-slate-200 rounded px-2" data-testid={`${testId}-x`} /></label>
            <label className="flex items-center gap-1"><span className="text-slate-500">Y %</span>
              <input type="number" min="0" max="100" value={y} onChange={(e) => setP({ y: Math.min(100, Math.max(0, +e.target.value || 0)) })} className="w-16 h-8 border border-slate-200 rounded px-2" data-testid={`${testId}-y`} /></label>
            <button type="button" className="ml-auto text-slate-400 hover:text-slate-600 underline" onClick={() => onPos({ x: 50, y: 50, fit: "cover", zoom: 1 })} data-testid={`${testId}-reset`}>Réinitialiser</button>
          </div>
          <p className="text-[11px] text-slate-400">Cliquez sur l'aperçu pour définir le point central de l'image.</p>
        </div>
      )}
      <MediaPicker open={open} onOpenChange={setOpen} onSelect={(id) => onChange(id)} />
    </div>
  );
}
